import { z } from 'zod';
// =============================================================================
// Chat links - buttons under a reply that open a page in the app (web chat)
// =============================================================================
/**
 * At most two links under one reply, and labels at most 20 characters: WhatsApp's
 * call-to-action limits, so a link can later be sent there as a URL button unchanged.
 */
export const MAX_CHAT_LINKS = 2;
export const MAX_CHAT_LINK_LABEL_CHARS = 20;
/**
 * A path inside the Bill web app, such as `/expenses/<id>`. Never a full URL: the web
 * client navigates within the app, and a channel that needs a URL adds its own origin.
 * The second character excludes `//` and `/\`, which a browser reads as another origin.
 */
const AppPathSchema = z.string().max(2048).regex(/^\/(?![/\\])\S*$/);
/**
 * A link offered under a reply. Unlike a button set, a link is never used up or
 * superseded: it opens the same page for as long as the message is in the thread.
 */
export const ChatLinkSchema = z.object({
    label: z.string().min(1).max(MAX_CHAT_LINK_LABEL_CHARS),
    path: AppPathSchema,
});
/** Stored as `metadata.links` on the reply's row. */
export const ChatLinksSchema = z.array(ChatLinkSchema).min(1).max(MAX_CHAT_LINKS);
/**
 * The links a message's metadata holds, or none. Metadata is untyped JSON at rest, so a
 * row that predates links or carries a malformed `links` reads as a plain reply rather
 * than failing the reader.
 */
export function messageLinks(metadata) {
    if (!metadata || typeof metadata !== 'object' || !('links' in metadata))
        return [];
    const parsed = ChatLinksSchema.safeParse(metadata.links);
    return parsed.success ? parsed.data : [];
}
